import { Modal, Typography, Space, Divider, Empty } from "antd";
import { CSSProperties, ReactNode } from "react";
import { colors } from "config/theme";

const { Title } = Typography;

interface ShowModalProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number | string;
  footer?: ReactNode;
  empty?: boolean;
  emptyText?: string;
}

const modalStyles = {
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  } as CSSProperties,
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.containerBg,
    color: colors.siderBg,
    fontSize: 18,
  } as CSSProperties,
  titleGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
  } as CSSProperties,
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
  } as CSSProperties,
  subtitle: {
    fontSize: 13,
    color: colors.textTertiary,
    margin: 0,
  } as CSSProperties,
  content: {
    padding: "8px 0",
  } as CSSProperties,
  itemDivider: {
    margin: "20px 0",
    borderColor: colors.borderLight,
  } as CSSProperties,
};

export const ShowModal: React.FC<ShowModalProps> = ({
  title,
  subtitle,
  icon,
  open,
  onClose,
  children,
  width = 600,
  footer,
  empty = false,
  emptyText = "No data available",
}) => {
  return (
    <Modal
      open={open}
      onOk={onClose}
      onCancel={onClose}
      width={width}
      footer={footer}
      title={
        <div style={modalStyles.header}>
          {icon && <div style={modalStyles.iconWrapper}>{icon}</div>}
          <div style={modalStyles.titleGroup}>
            <Title level={4} style={modalStyles.title}>
              {title}
            </Title>
            {subtitle && (
              <span style={modalStyles.subtitle}>{subtitle}</span>
            )}
          </div>
        </div>
      }
      styles={{
        body: {
          padding: "8px 0",
          maxHeight: "60vh",
          overflowY: "auto",
        },
      }}
    >
      {empty ? (
        <Empty description={emptyText} />
      ) : (
        <div style={modalStyles.content}>{children}</div>
      )}
    </Modal>
  );
};

// Helper component for modal items with dividers
interface ShowModalItemProps {
  title?: string;
  children: ReactNode;
  showDivider?: boolean;
}

export const ShowModalItem: React.FC<ShowModalItemProps> = ({
  title,
  children,
  showDivider = true,
}) => {
  return (
    <>
      {title && (
        <Divider plain style={{ ...modalStyles.itemDivider, marginTop: 0 }}>
          <span style={{ fontWeight: 600, color: colors.textPrimary }}>
            {title}
          </span>
        </Divider>
      )}
      {children}
      {showDivider && !title && (
        <Divider style={modalStyles.itemDivider} />
      )}
    </>
  );
};

export default ShowModal;
